import {hpcAttrs} from '@github-ui/hpc'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useQuery} from '@github-ui/react-query'
import {SoftNavOnlyLink as Link, useParams} from '@github-ui/react-router'
import {useRepoIssuesSidebarLayoutEnabled} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
import {useSearchParams} from '@github-ui/use-navigate'
import {AlertIcon, BookIcon} from '@primer/octicons-react'
import {Button, Heading} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {useCallback, useEffect, useMemo, useRef} from 'react'

import {BUTTON_LABELS, LABELS} from '../../constants'
import {useSavedViewEndpoints, useSavedViewScope} from '../../contexts/SavedViewEndpointsContext'
import {SAVED_VIEW_PIN_SOURCES, useSavedViewAnalytics} from '../../hooks/use-saved-view-analytics'
import {useSavedViewPinControls} from '../../hooks/use-saved-view-pin-controls'
import {savedViewsQueryOptions} from '../../queries'
import type {SavedViewPlacementOwnerType, SavedViewsResponse} from '../../types'
import {canShowCreateAction} from '../../utils/can-show-create-action'
import {SavedViewMutationErrorBanner} from '../dialogs/SavedViewMutationErrorBanner'
import styles from './RepositoryViews.module.css'
import {DEFAULT_VIEWS_SORT, isViewsSortValue, sortViews, type ViewsSortValue} from './views-sort-options'
import {ViewsList} from './ViewsList'
import {ViewsSections} from './ViewsSections'

interface ViewsIndexContentProps {
  newViewUrl: string
  owner?: string
  repo?: string
  placementOwnerType?: SavedViewPlacementOwnerType
  pinEnabled?: boolean
  teamPersonalEnabled?: boolean
  sidebarLayoutEnabled?: boolean
  headingVisuallyHidden?: boolean
  initialData?: SavedViewsResponse
}

export function ViewsIndexContent({
  newViewUrl,
  owner: ownerProp,
  repo: repoProp,
  placementOwnerType,
  pinEnabled,
  teamPersonalEnabled: teamPersonalEnabledProp,
  sidebarLayoutEnabled: sidebarLayoutEnabledProp,
  headingVisuallyHidden = false,
  initialData,
}: ViewsIndexContentProps) {
  const {owner: routeOwner = '', repo: routeRepo = ''} = useParams<{owner: string; repo: string}>()
  const owner = ownerProp ?? routeOwner
  const repo = repoProp ?? routeRepo
  const [searchParams, setSearchParams] = useSearchParams()
  const sortParam = searchParams.get('sort')
  const sort = isViewsSortValue(sortParam) ? sortParam : DEFAULT_VIEWS_SORT
  const repositorySidebarLayoutEnabled = useRepoIssuesSidebarLayoutEnabled()
  const sidebarLayoutEnabled = sidebarLayoutEnabledProp ?? repositorySidebarLayoutEnabled

  const handleSortChange = useCallback(
    (next: ViewsSortValue) => {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev)
          if (next === DEFAULT_VIEWS_SORT) {
            params.delete('sort')
          } else {
            params.set('sort', next)
          }
          return params
        },
        {replace: true, preventAutofocus: true},
      )
    },
    [setSearchParams],
  )

  const {trackIndexVisited} = useSavedViewAnalytics()
  const hasTrackedVisitRef = useRef(false)
  useEffect(() => {
    if (hasTrackedVisitRef.current) return
    hasTrackedVisitRef.current = true
    trackIndexVisited(placementOwnerType ? {placementOwnerType, searchType: 'issues'} : undefined)
  }, [placementOwnerType, trackIndexVisited])

  const {listUrl} = useSavedViewEndpoints(owner, repo)
  const {data, isError, isFetching, refetch} = useQuery(savedViewsQueryOptions({owner, repo, listUrl, initialData}))
  // eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags -- issue_pinned_views uses actor-aware server-side evaluation via PinnedViewsFeature; isFeatureEnabled only knows current_user.
  const featureFlagPinEnabled = useFeatureFlag('issue_pinned_views')
  const resolvedPinEnabled = pinEnabled ?? featureFlagPinEnabled
  // eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags -- issue_pinned_views_team_vs_personal uses actor-aware server-side evaluation via PinnedViewsFeature; isFeatureEnabled only knows current_user.
  const featureFlagTeamPersonalEnabled = useFeatureFlag('issue_pinned_views_team_vs_personal')
  const teamPersonalEnabled = teamPersonalEnabledProp ?? featureFlagTeamPersonalEnabled
  const viewScope = useSavedViewScope()
  const sortedViews = useMemo(
    () => sortViews(data?.savedViews ?? [], sort, {pinnedFirst: resolvedPinEnabled}),
    [data?.savedViews, sort, resolvedPinEnabled],
  )
  const {pinControls, pinErrorMessage, retryPin, isPinRetrying} = useSavedViewPinControls({
    owner,
    repo,
    enabled: resolvedPinEnabled,
    source: SAVED_VIEW_PIN_SOURCES.indexRowMenu,
    initialData,
    perVisibilityPinCapEnabled: teamPersonalEnabled,
  })
  const isLoading = isFetching && !data && !isError
  const showError = isError && !data
  const viewerCanManageViews = data?.viewerCanManageViews ?? false
  const showCreateAction = canShowCreateAction({savedViews: data, teamPersonalEnabled})

  const errorBlankslate = (
    <Blankslate spacious>
      <Blankslate.Visual>
        <AlertIcon size="medium" />
      </Blankslate.Visual>
      <Blankslate.Heading>{LABELS.views.indexErrorTitle}</Blankslate.Heading>
      <Blankslate.Description>{LABELS.views.indexLoadErrorDescription}</Blankslate.Description>
      <Blankslate.PrimaryAction onClick={() => refetch()}>{BUTTON_LABELS.tryAgain}</Blankslate.PrimaryAction>
    </Blankslate>
  )

  const renderErrorFallback = (
    <Blankslate spacious>
      <Blankslate.Visual>
        <AlertIcon size="medium" />
      </Blankslate.Visual>
      <Blankslate.Heading>{LABELS.views.indexErrorTitle}</Blankslate.Heading>
      <Blankslate.Description>{LABELS.views.indexDisplayErrorDescription}</Blankslate.Description>
      <Blankslate.PrimaryAction onClick={() => window.location.reload()}>
        {BUTTON_LABELS.reloadPage}
      </Blankslate.PrimaryAction>
    </Blankslate>
  )

  const emptyBlankslate = (
    <Blankslate spacious>
      <Blankslate.Visual>
        <BookIcon size="medium" />
      </Blankslate.Visual>
      <Blankslate.Heading>{LABELS.views.emptyHeading}</Blankslate.Heading>
      <Blankslate.Description>{LABELS.views.emptyDescription(viewScope)}</Blankslate.Description>
      {viewerCanManageViews && (
        <Button as={Link} to={newViewUrl} variant="primary" className={styles.blankslateAction}>
          {BUTTON_LABELS.newView}
        </Button>
      )}
    </Blankslate>
  )

  const listProps = {
    views: sortedViews,
    owner,
    repo,
    sort,
    onSortChange: handleSortChange,
    viewerCanManageViews,
    showPinControls: resolvedPinEnabled,
    pinControls,
  }

  return (
    <div className={clsx(styles.viewsPage, sidebarLayoutEnabled && 'mt-0')}>
      {!sidebarLayoutEnabled && (
        <>
          {headingVisuallyHidden && !showCreateAction ? (
            <Heading as="h1" className="sr-only">
              {LABELS.viewsTitle}
            </Heading>
          ) : (
            <div className={clsx(styles.headerRow, headingVisuallyHidden && styles.headerRowVisuallyHiddenHeading)}>
              <Heading as="h1" className={headingVisuallyHidden ? 'sr-only' : styles.heading}>
                {LABELS.viewsTitle}
              </Heading>
              {showCreateAction && (
                <Button as={Link} to={newViewUrl} variant="primary">
                  {BUTTON_LABELS.newView}
                </Button>
              )}
            </div>
          )}
        </>
      )}
      <SavedViewMutationErrorBanner
        message={pinErrorMessage}
        onRetry={retryPin}
        isRetrying={isPinRetrying}
        focusOnMount={false}
      />
      <ErrorBoundary boundaryName="RepositoryViewsIndexPage" fallback={renderErrorFallback}>
        {teamPersonalEnabled ? (
          <div {...hpcAttrs()} aria-busy={isLoading}>
            {isLoading ? (
              <div className={styles.viewsListWrapper}>
                <ViewsList {...listProps} isLoading />
              </div>
            ) : showError ? (
              <div className={styles.viewsListWrapper}>{errorBlankslate}</div>
            ) : (
              <ViewsSections
                views={data?.savedViews ?? []}
                owner={owner}
                repo={repo}
                newViewUrl={newViewUrl}
                viewerCanManageViews={viewerCanManageViews}
                showPinControls={resolvedPinEnabled}
                pinControls={pinControls}
              />
            )}
          </div>
        ) : (
          <div className={styles.viewsListWrapper} {...hpcAttrs()} aria-busy={isLoading}>
            {isLoading ? (
              <ViewsList {...listProps} isLoading />
            ) : showError ? (
              errorBlankslate
            ) : (
              <ViewsList {...listProps} emptyState={emptyBlankslate} />
            )}
          </div>
        )}
      </ErrorBoundary>
    </div>
  )
}
